// Component: Profile.jsx
// Purpose: Account identity, trading preferences and alerts.
//
// Rebuilt on the DS. This page was a MUI/DS hybrid (Box / Typography / Paper /
// Grid / Avatar / Divider / Slider / CircularProgress alongside a few DS bits),
// which is exactly why it read off-system next to the rest of the app. Every MUI
// import is gone; the page is now DS primitives + Tailwind tokens only.
//
// ONE responsive page (no separate mobile build): the field grids are
// `grid-cols-1 md:grid-cols-2`, so they stack on a phone, and the identity card
// and toggle rows are already fluid. On phone the app shell supplies the
// "Profile" header, so the DS PageHeader is desktop-only (`hidden lg:block`) —
// otherwise you'd read "Profile" twice.
//
// BEHAVIOUR IS PRESERVED VERBATIM: the same formData shape, the same
// user -> form mapping with all its fallbacks, the same updateUserProfile
// payload, and the same push-notification flows (enable/disable, local test,
// server test, hard reset).

import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useBreakpoint } from "../../hooks/useBreakpoint";
import api from "../../services/api";
import {
  askNotificationPermission,
  subscribeToPushNotifications,
  unsubscribeFromPushNotifications,
} from "../../utils/pushNotifications";
import {
  PageHeader,
  Tabs,
  Input,
  Select,
  Textarea,
  Toggle,
  Slider,
  Loader,
  Icon,
  useToast,
} from "../../components/ds";

//--> A titled group of fields. Sections carry the page's rhythm so each tab
//    reads as a short, scannable form rather than one long wall of inputs.
const Section = ({ title, hint, children, className = "" }) => (
  <section className={`rounded-2xl border border-[var(--divider)] bg-bg-2 p-4 md:p-5 ${className}`}>
    {(title || hint) && (
      <header className="mb-4">
        {title && (
          <h2 className="text-[10px] font-bold uppercase tracking-[.10em] text-fg-3">{title}</h2>
        )}
        {hint && <p className="mt-1 text-xs text-fg-3">{hint}</p>}
      </header>
    )}
    {children}
  </section>
);

//--> Label + description on the left, control on the right. The alert rows use
//    this so they read as settings, not as a row of loose switches.
const SettingRow = ({ title, hint, children, last = false }) => (
  <div
    className={`flex items-center justify-between gap-4 py-3 ${
      last ? "" : "border-b border-[var(--divider)]"
    }`}
  >
    <div className="min-w-0">
      <div className="text-sm font-medium text-fg-1">{title}</div>
      {hint && <div className="mt-0.5 text-xs text-fg-3">{hint}</div>}
    </div>
    <div className="shrink-0">{children}</div>
  </div>
);

//--> Group heading for the wide layout, where the three groups sit side by side
//    and the tabs are gone — each column still needs to say what it is.
const GroupHeading = ({ children }) => (
  <h2 className="px-1 text-sm font-semibold text-fg-1">{children}</h2>
);

const Profile = () => {
  const { user, updateUserProfile, loading } = useAuth();
  //--> Tabs earn their keep on a phone/tablet (one group at a time beats a long
  //    scroll). On a large screen they just hide two thirds of the page behind
  //    clicks while the monitor sits empty — so above lg we drop the tabs and
  //    show every group at once, in columns.
  const { isPhone, isTablet } = useBreakpoint();
  const compact = isPhone || isTablet;
  const [activeTab, setActiveTab] = useState(0);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    displayName: "",
    bio: "",
    phoneNumber: "",
    address: "",
    investmentStyle: "balanced",
    riskTolerance: 5,
    yearsExperience: 0,
    defaultCurrency: "USD",
    dashboardLayout: "default",
    notifications: {
      priceAlerts: true,
      newsAlerts: true,
      tradeAlerts: true,
    },
  });
  const [message, setMessage] = useState("");
  const [severity, setSeverity] = useState("info");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);

  // Push Notification State
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);

  //--> DS toast. Bridge the legacy setShowSnackbar(...) triggers so every
  //    existing call-site keeps working.
  const toast = useToast();
  useEffect(() => {
    if (showSnackbar && message) {
      (toast[severity] || toast.info)(message);
      setShowSnackbar(false);
    }
  }, [showSnackbar, message, severity, toast]);

  useEffect(() => {
    // Check if service worker is ready and we have a subscription
    const checkSubscription = async () => {
      try {
        if (!("serviceWorker" in navigator)) return;
        const registration = await navigator.serviceWorker.ready;
        if (!registration.pushManager) return;

        const subscription = await registration.pushManager.getSubscription();
        if (subscription) {
          setPushEnabled(true);
          // Auto-sync with backend to ensure DB is up to date
          subscribeToPushNotifications().catch((e) => console.error("Sync failed:", e));
        } else {
          setPushEnabled(false);
        }
      } catch (error) {
        console.error("Error checking subscription:", error);
      }
    };
    checkSubscription();
  }, []);

  useEffect(() => {
    if (user) {
      //--> Fallback chains preserved verbatim: the backend has shipped these
      //    fields under several shapes over time (top level, profile_data,
      //    preferences, snake_case and camelCase).
      setFormData((prevData) => ({
        ...prevData,
        firstName: user.first_name || user.firstName || "",
        lastName: user.last_name || user.lastName || "",
        displayName: user.display_name || user.displayName || "",
        bio: user.bio || "",
        phoneNumber:
          user.phone ||
          user.phoneNumber ||
          (user.profile_data && (user.profile_data.phone || user.profile_data.phoneNumber)) ||
          "",
        address: user.address || (user.profile_data && user.profile_data.address) || "",
        investmentStyle:
          user.investment_style ||
          user.investmentStyle ||
          (user.preferences && user.preferences.investmentStyle) ||
          "balanced",
        riskTolerance:
          user.risk_tolerance ||
          user.riskTolerance ||
          (user.preferences && user.preferences.riskTolerance) ||
          5,
        yearsExperience:
          user.years_experience ||
          user.yearsExperience ||
          (user.preferences && user.preferences.yearsExperience) ||
          0,
        defaultCurrency:
          user.default_currency ||
          user.defaultCurrency ||
          (user.preferences && user.preferences.defaultCurrency) ||
          "USD",
        dashboardLayout:
          user.dashboard_layout ||
          user.dashboardLayout ||
          (user.preferences && user.preferences.dashboardLayout) ||
          "default",
        notifications: {
          priceAlerts:
            (user.notifications && user.notifications.priceAlerts) ||
            (user.notification_settings && user.notification_settings.priceAlerts) ||
            true,
          newsAlerts:
            (user.notifications && user.notifications.newsAlerts) ||
            (user.notification_settings && user.notification_settings.newsAlerts) ||
            true,
          tradeAlerts:
            (user.notifications && user.notifications.tradeAlerts) ||
            (user.notification_settings && user.notification_settings.tradeAlerts) ||
            true,
        },
      }));
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSwitchChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      notifications: { ...prev.notifications, [name]: checked },
    }));
  };

  const handleSliderChange = (value) => {
    setFormData((prev) => ({ ...prev, riskTolerance: value }));
  };

  const handlePushToggle = async (checked) => {
    setPushLoading(true);
    try {
      if (checked) {
        const permission = await askNotificationPermission();
        if (permission === "granted") {
          await subscribeToPushNotifications();
          setPushEnabled(true);
          setSeverity("success");
          setMessage("Push notifications enabled!");
        } else {
          setSeverity("warning");
          setMessage("Notification permission denied.");
        }
      } else {
        await unsubscribeFromPushNotifications();
        setPushEnabled(false);
        setSeverity("info");
        setMessage("Push notifications disabled.");
      }
      setShowSnackbar(true);
    } catch (error) {
      console.error(error);
      setSeverity("error");
      setMessage("Failed to update notification settings.");
      setShowSnackbar(true);
    } finally {
      setPushLoading(false);
    }
  };

  const handleTestLocal = async () => {
    try {
      const registration = await navigator.serviceWorker.ready;
      await registration.showNotification("Local Test", {
        body: "If you see this, notifications are working!",
        icon: "/logo192.png",
      });
      setSeverity("success");
      setMessage("Local test sent.");
      setShowSnackbar(true);
    } catch (err) {
      console.error(err);
      setSeverity("error");
      setMessage("Failed to show local notification.");
      setShowSnackbar(true);
    }
  };

  const handleTestServer = async () => {
    try {
      const token = localStorage.getItem("token");
      await api.post("/notifications/test", {}, { headers: { Authorization: `Bearer ${token}` } });
      setSeverity("success");
      setMessage("Test Sent! Check your notifications.");
      setShowSnackbar(true);
    } catch (e) {
      console.error(e);
      setSeverity("error");
      setMessage("Test failed: " + e.message);
      setShowSnackbar(true);
    }
  };

  const handleHardReset = async () => {
    try {
      setSeverity("info");
      setMessage("Resetting subscription...");
      setShowSnackbar(true);

      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) await subscription.unsubscribe();

      // Force new subscription
      await subscribeToPushNotifications();

      setSeverity("success");
      setMessage("Hard Reset Complete! Try Server Test now.");
      setShowSnackbar(true);
    } catch (e) {
      console.error(e);
      setSeverity("error");
      setMessage("Reset failed: " + e.message);
      setShowSnackbar(true);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await updateUserProfile({
        profile_data: {
          ...formData,
          // Explicitly map phoneNumber to phone for backend compatibility
          phone: formData.phoneNumber,
          preferences: { dashboardLayout: formData.dashboardLayout },
          notification_settings: formData.notifications,
        },
        // Also include at the top level for auth service
        phoneNumber: formData.phoneNumber,
        address: formData.address,
        firstName: formData.firstName,
        lastName: formData.lastName,
      });
      setSeverity("success");
      setMessage("Profile updated successfully!");
      setShowSnackbar(true);
    } catch (error) {
      setSeverity("error");
      setMessage(error.message || "Failed to update profile");
      setShowSnackbar(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center px-6 text-center">
        <p className="text-sm text-fg-3">Unable to load profile data. Please try again later.</p>
      </div>
    );
  }

  const fullName =
    [formData.firstName, formData.lastName].filter(Boolean).join(" ") ||
    formData.displayName ||
    user.username ||
    "Your account";
  const initials =
    (formData.firstName?.[0] || user.email?.[0] || "?").toUpperCase() +
    (formData.lastName?.[0] || "").toUpperCase();
  const plan = user.subscription_plan || user.plan;

  return (
    <div className="min-h-screen bg-main-bg px-4 pb-10 dark:bg-main-dark-bg md:px-6">
      {/* Desktop only — on phone the app shell already renders "Profile". */}
      <div className="hidden lg:block">
        <PageHeader title="Profile" subtitle="Your account, preferences and alerts" />
      </div>

      {/* Narrow reads best for a single stacked form; once every group is on
          screen at lg+ we want the width back. */}
      <div className="mx-auto w-full max-w-3xl lg:max-w-7xl">
        {/* Identity */}
        <div className="mt-4 flex items-center gap-4 rounded-2xl border border-[var(--divider)] bg-bg-2 p-4 md:mt-0 md:p-5">
          <div
            className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full text-lg font-semibold md:h-16 md:w-16 md:text-xl"
            style={{
              background:
                "linear-gradient(135deg, var(--color-accent), color-mix(in srgb, var(--color-accent) 55%, #000))",
              color: "var(--fg-on-accent)",
            }}
            aria-hidden="true"
          >
            {initials}
          </div>
          <div className="min-w-0 flex-1">
            <div className="truncate text-base font-semibold text-fg-1 md:text-lg">{fullName}</div>
            <div className="truncate text-xs text-fg-3">{user.email}</div>
            <div className="mt-2 flex flex-wrap gap-2">
              {plan && <span className="ds-badge xs profit uppercase">{plan}</span>}
              {user.username && <span className="ds-badge xs neut">@{user.username}</span>}
            </div>
          </div>
        </div>

        {/* Tabs only where space is tight. */}
        {compact && (
          <Tabs
            className="mt-5"
            value={activeTab}
            onChange={setActiveTab}
            tabs={[{ label: "Personal" }, { label: "Trading" }, { label: "Settings" }]}
          />
        )}

        <form onSubmit={handleSubmit} className="mt-5">
          {/* compact → one group, stacked. wide → every group side by side, so a
              big monitor shows the whole page instead of a narrow third of it
              with the other two thirds hidden behind clicks. Each group is
              already a flex column, so it becomes a grid column for free. */}
          <div
            className={
              compact
                ? "flex flex-col gap-4"
                : "grid grid-cols-1 items-start gap-5 lg:grid-cols-2 2xl:grid-cols-3"
            }
          >
          {/*>>> PERSONAL <<<*/}
          {(!compact || activeTab === 0) && (
            <div className="flex flex-col gap-4">
              {!compact && <GroupHeading>Personal</GroupHeading>}
              <Section title="Identity" hint="How you appear across DeeTrade.">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <Input label="First Name" name="firstName" value={formData.firstName} onChange={handleChange} />
                  <Input label="Last Name" name="lastName" value={formData.lastName} onChange={handleChange} />
                  <div className="md:col-span-2">
                    <Input label="Display Name" name="displayName" value={formData.displayName} onChange={handleChange} />
                  </div>
                  <div className="md:col-span-2">
                    <Textarea label="Bio" name="bio" value={formData.bio} onChange={handleChange} rows={3} />
                  </div>
                </div>
              </Section>

              <Section title="Contact">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <Input label="Phone Number" name="phoneNumber" value={formData.phoneNumber} onChange={handleChange} />
                  <Input label="Address" name="address" value={formData.address} onChange={handleChange} />
                </div>
              </Section>
            </div>
          )}

          {/*>>> TRADING <<<*/}
          {(!compact || activeTab === 1) && (
            <div className="flex flex-col gap-4">
              {!compact && <GroupHeading>Trading</GroupHeading>}
              <Section title="Style" hint="Used to tailor defaults and suggestions.">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <Select label="Investment Style" name="investmentStyle" value={formData.investmentStyle} onChange={handleChange}>
                    <option value="conservative">Conservative</option>
                    <option value="balanced">Balanced</option>
                    <option value="aggressive">Aggressive</option>
                  </Select>
                  <Select
                    label="Trading Frequency"
                    name="tradingFrequency"
                    value={formData.tradingFrequency || "daily"}
                    onChange={handleChange}
                  >
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                  </Select>
                  <Input
                    label="Years of Experience"
                    name="yearsExperience"
                    type="number"
                    min={0}
                    value={formData.yearsExperience}
                    onChange={handleChange}
                  />
                </div>
              </Section>

              <Section title="Risk" hint="Where you sit between capital preservation and upside.">
                <Slider
                  label="Risk tolerance"
                  value={formData.riskTolerance}
                  min={1}
                  max={10}
                  step={1}
                  suffix="/ 10"
                  onChange={handleSliderChange}
                  minLabel="Conservative"
                  maxLabel="Aggressive"
                />
              </Section>
            </div>
          )}

          {/*>>> SETTINGS <<<*/}
          {(!compact || activeTab === 2) && (
            <div className="flex flex-col gap-4">
              {!compact && <GroupHeading>Settings</GroupHeading>}
              <Section title="Preferences">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <Select label="Default Currency" name="defaultCurrency" value={formData.defaultCurrency} onChange={handleChange}>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                    <option value="JPY">JPY</option>
                  </Select>
                  <Select label="Dashboard Layout" name="dashboardLayout" value={formData.dashboardLayout} onChange={handleChange}>
                    <option value="default">Default</option>
                    <option value="compact">Compact</option>
                    <option value="expanded">Expanded</option>
                  </Select>
                </div>
              </Section>

              <Section title="Alerts" hint="What DeeTrade tells you about.">
                <SettingRow title="Price alerts" hint="When a watched instrument moves">
                  <Toggle
                    checked={formData.notifications?.priceAlerts}
                    onChange={(v) => handleSwitchChange({ target: { name: "priceAlerts", checked: v } })}
                  />
                </SettingRow>
                <SettingRow title="News alerts" hint="Market news on your instruments">
                  <Toggle
                    checked={formData.notifications?.newsAlerts}
                    onChange={(v) => handleSwitchChange({ target: { name: "newsAlerts", checked: v } })}
                  />
                </SettingRow>
                <SettingRow title="Trade alerts" hint="Fills, rejections and order updates" last>
                  <Toggle
                    checked={formData.notifications?.tradeAlerts}
                    onChange={(v) => handleSwitchChange({ target: { name: "tradeAlerts", checked: v } })}
                  />
                </SettingRow>
              </Section>

              <Section
                title="Push notifications"
                hint="Real-time alerts on this device. Requires browser permission."
              >
                <SettingRow
                  title={
                    <span className="flex items-center gap-2">
                      <Icon name="notify" size={16} />
                      Enable on this device
                      {pushLoading && <Loader size="sm" />}
                    </span>
                  }
                  hint={pushEnabled ? "This device is subscribed" : "Not subscribed"}
                  last
                >
                  <Toggle checked={pushEnabled} disabled={pushLoading} onChange={handlePushToggle} />
                </SettingRow>

                {/* Diagnostics — kept from the previous page (local/server test and
                    the hard reset), just grouped instead of scattered. */}
                <div className="mt-4 flex flex-wrap gap-2 border-t border-[var(--divider)] pt-4">
                  {pushEnabled && (
                    <>
                      <button type="button" onClick={handleTestLocal} className="btn btn-secondary btn-sm">
                        Test local
                      </button>
                      <button type="button" onClick={handleTestServer} className="btn btn-secondary btn-sm">
                        Send server test
                      </button>
                    </>
                  )}
                  <button type="button" onClick={handleHardReset} className="btn btn-danger btn-sm ml-auto">
                    Hard reset push
                  </button>
                </div>
              </Section>
            </div>
          )}

          </div>

          {/* Save — full-width on phone (thumb-reachable), inline on desktop. */}
          <div className="mt-6 flex justify-end border-t border-[var(--divider)] pt-4">
            <button
              type="submit"
              className="btn btn-primary w-full justify-center font-semibold sm:w-auto sm:min-w-[160px]"
              disabled={isSubmitting}
            >
              <Icon name="save" size={16} />
              {isSubmitting ? "Saving…" : "Save changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Profile;
